/**
 * ThemeToggle - UI-Komponente für Light/Dark Mode Switch
 * 
 * Zweck:
 * - Toggle zwischen ☀️ Light / 🌙 Dark / 🤖 Auto
 * - Visuelles Feedback für aktuelles Theme
 * - Touch-optimiert (56px Touch-Targets)
 * 
 * Kontext:
 * - Wird im Header ALLER Screens angezeigt
 * - Deep Research: Light Mode +250% Outdoor-Lesbarkeit
 * - Sprint 2 Task 1: P0 Critical für Demo morgen
 * 
 * Design:
 * - 3 Buttons: Sun (Light), Moon (Dark), Auto (System)
 * - Active State: Primary Color + Bold
 * - Inactive State: Gray + Normal Weight
 * - Mobile: 56px min-height für Touch-Targets
 */

import React from 'react';
import { useTheme } from '../contexts/ThemeContext';

const ThemeToggle: React.FC = () => {
  const { theme, setTheme } = useTheme();

  return (
    <div className="flex items-center space-x-2 bg-gray-200 dark:bg-gray-700 rounded-lg p-1">
      {/* Light Mode Button */}
      <button
        onClick={() => setTheme('light')}
        className={`
          flex items-center justify-center px-3 py-2 rounded-md transition-all
          min-h-[48px] min-w-[48px]
          ${theme === 'light' 
            ? 'bg-white dark:bg-gray-600 text-primary-600 font-semibold shadow-sm' 
            : 'text-gray-600 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
          }
        `}
        aria-label="Light Mode aktivieren"
        title="Light Mode (Outdoor-optimiert)"
      >
        <span className="text-xl" role="img" aria-label="Sonne">☀️</span>
      </button>

      {/* Dark Mode Button */}
      <button
        onClick={() => setTheme('dark')}
        className={`
          flex items-center justify-center px-3 py-2 rounded-md transition-all
          min-h-[48px] min-w-[48px]
          ${theme === 'dark' 
            ? 'bg-gray-800 text-primary-400 font-semibold shadow-sm' 
            : 'text-gray-600 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
          }
        `}
        aria-label="Dark Mode aktivieren"
        title="Dark Mode (Augen schonen)"
      >
        <span className="text-xl" role="img" aria-label="Mond">🌙</span>
      </button>

      {/* Auto Mode Button */}
      <button
        onClick={() => setTheme('auto')}
        className={`
          flex items-center justify-center px-3 py-2 rounded-md transition-all
          min-h-[48px] min-w-[48px]
          ${theme === 'auto' 
            ? 'bg-white dark:bg-gray-600 text-primary-600 font-semibold shadow-sm' 
            : 'text-gray-600 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
          }
        `}
        aria-label="Auto Mode aktivieren"
        title="Auto Mode (Tageszeit-basiert: 06:00-20:00 = Light)"
      >
        <span className="text-xl" role="img" aria-label="Automatisch">🤖</span>
      </button>
    </div>
  );
};

export default ThemeToggle;
